import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import moment from "moment";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  TicketAccess,
  TicketDetail,
  TicketSummary,
  getTicketAccess,
  getTickets,
  ticketErrorMessage,
} from "../../../utils/icTicket";
import { useViewport } from "../../../utils/useViewport";
import NewTicketDialog from "./NewTicketDialog";
import {
  CARD_BORDER,
  NeedsReplyBadge,
  PRIMARY,
  PriorityChip,
  StatusChip,
  TICKET_FILTERS,
  TicketFilterKey,
} from "./ticketStatus";

const ticketUrl = (t: TicketSummary) =>
  `/dashboard/support/${encodeURIComponent(t.displayId || t.id)}`;

const shortDate = (iso: string) => (iso ? moment(iso).format("MMM D, YYYY") : "—");

// /dashboard/support — the user's support tickets (managers: every Thrive
// ticket), served by IC_Ticket through Thrive's server.
const SupportTickets = () => {
  const navigate = useNavigate();
  const { isPhone } = useViewport();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterKey: TicketFilterKey =
    TICKET_FILTERS.find((f) => f.key === searchParams.get("filter"))?.key ?? "all";
  const filter = TICKET_FILTERS.find((f) => f.key === filterKey)!;

  const [items, setItems] = useState<TicketSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [access, setAccess] = useState<TicketAccess | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  // A slow earlier response must never overwrite a newer filter's results.
  const requestIdRef = useRef(0);
  const statusParam = filter.statuses.join(",");

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    getTickets({ status: statusParam || undefined })
      .then((page) => {
        if (requestId !== requestIdRef.current) return;
        setItems(page.items ?? []);
        setNextCursor(page.nextCursor ?? null);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load support tickets:", err);
        setItems([]);
        setNextCursor(null);
        setError(ticketErrorMessage(err));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [statusParam]);

  // Only to word the subtitle; the server enforces who sees what.
  useEffect(() => {
    getTicketAccess()
      .then(setAccess)
      .catch(() => setAccess(null));
  }, []);

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    getTickets({ status: statusParam || undefined, cursor: nextCursor })
      .then((page) => {
        if (requestId !== requestIdRef.current) return;
        setItems((prev) => {
          const seen = new Set(prev.map((t) => t.id));
          return [...prev, ...(page.items ?? []).filter((t) => !seen.has(t.id))];
        });
        setNextCursor(page.nextCursor ?? null);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        setError(ticketErrorMessage(err));
      })
      .finally(() => setLoadingMore(false));
  };

  const changeFilter = (key: TicketFilterKey | null) => {
    if (!key) return;
    const params = new URLSearchParams(searchParams);
    if (key === "all") params.delete("filter");
    else params.set("filter", key);
    setSearchParams(params, { replace: true });
  };

  const rows = useMemo(
    () => (filter.needsReplyOnly ? items.filter((t) => t.needsReply) : items),
    [items, filter.needsReplyOnly],
  );
  const replyCount = useMemo(() => items.filter((t) => t.needsReply).length, [items]);

  const onCreated = (ticket: TicketDetail) => {
    setNewOpen(false);
    navigate(ticketUrl(ticket));
  };

  const emptyText = error
    ? "No tickets to show."
    : filterKey === "all"
      ? "No support tickets yet. Use New ticket to report a problem."
      : `No tickets in "${filter.label}".`;

  const subtitle =
    access === "project"
      ? "Every Thrive support ticket. Newest first."
      : "Tickets you have filed with the Thrive support team. Newest first.";

  return (
    <Box sx={{ padding: "20px", textAlign: "left" }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        justifyContent="space-between"
        useFlexGap
        sx={{ flexWrap: "wrap", mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#2c2c34" }}>
            Support
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewOpen(true)}
          sx={{ backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7d184e" } }}
        >
          New ticket
        </Button>
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        useFlexGap
        sx={{ flexWrap: "wrap", mb: 2 }}
      >
        {isPhone ? (
          <TextField
            select
            size="small"
            label="Show"
            value={filterKey}
            onChange={(e) => changeFilter(e.target.value as TicketFilterKey)}
            sx={{ minWidth: 220 }}
          >
            {TICKET_FILTERS.map((f) => (
              <MenuItem key={f.key} value={f.key}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filterKey}
            onChange={(_, key) => changeFilter(key)}
            aria-label="Filter tickets by status"
          >
            {TICKET_FILTERS.map((f) => (
              <ToggleButton
                key={f.key}
                value={f.key}
                sx={{ textTransform: "none", whiteSpace: "nowrap", px: 1.5 }}
              >
                {f.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {!loading && filterKey !== "reply" && replyCount > 0 ? (
          <Button
            size="small"
            onClick={() => changeFilter("reply")}
            sx={{ textTransform: "none", color: "#c62828", fontWeight: 700 }}
          >
            {replyCount} need{replyCount === 1 ? "s" : ""} your reply
          </Button>
        ) : null}
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{ border: CARD_BORDER, borderRadius: "12px", overflow: "auto" }}
      >
        {isPhone ? (
          <Box>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <CircularProgress sx={{ color: PRIMARY }} />
              </Box>
            ) : rows.length === 0 ? (
              <Typography sx={{ textAlign: "center", color: "#8a8a93", py: 5, px: 2 }}>
                {emptyText}
              </Typography>
            ) : (
              rows.map((t) => (
                <Box
                  key={t.id}
                  component={Link}
                  to={ticketUrl(t)}
                  sx={{
                    display: "block",
                    padding: "12px 16px",
                    borderBottom: CARD_BORDER,
                    color: "inherit",
                    textDecoration: "none",
                    "&:active": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: "#8a8a93", fontWeight: 600 }}>
                      {t.displayId}
                    </Typography>
                    <StatusChip ticket={t} />
                  </Stack>
                  <Typography sx={{ color: PRIMARY, fontWeight: 600, mt: 0.5 }}>
                    {t.title}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }} useFlexGap>
                    <PriorityChip priority={t.priority} />
                    <Typography variant="body2" sx={{ color: "#8a8a93" }}>
                      {t.createdBy || "—"} · Updated {shortDate(t.updatedAt)} · {t.commentCount} comment
                      {t.commentCount === 1 ? "" : "s"}
                    </Typography>
                  </Stack>
                </Box>
              ))
            )}
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, color: "#2c2c34", whiteSpace: "nowrap" } }}>
                <TableCell>ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell align="right">Comments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress sx={{ color: PRIMARY }} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", color: "#8a8a93", py: 5 }}>
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((t) => (
                  <TableRow
                    key={t.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(ticketUrl(t))}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap", color: "#5a5a66", fontWeight: 600 }}>
                      {t.displayId}
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: "wrap" }}>
                        <Link
                          to={ticketUrl(t)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: PRIMARY, fontWeight: 600, textDecoration: "none" }}
                        >
                          {t.title}
                        </Link>
                        {t.needsReply ? <NeedsReplyBadge /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <StatusChip ticket={t} />
                    </TableCell>
                    <TableCell>
                      <PriorityChip priority={t.priority} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{t.createdBy || "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{shortDate(t.updatedAt)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap", color: "#5a5a66" }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                        <span>{t.commentCount}</span>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {!loading && nextCursor ? (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            variant="outlined"
            onClick={loadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={16} /> : null}
            sx={{ color: PRIMARY, borderColor: PRIMARY }}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </Button>
        </Box>
      ) : null}

      <NewTicketDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={onCreated} />
    </Box>
  );
};

export default SupportTickets;
